import React, { useEffect, useState, useCallback, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import './App.css';
import PatientDashboard from './components/PatientDashboard';
import PatientCarePlan from './components/PatientCarePlan';
import ImportCarePlan from './components/ImportCarePlan';
// Receipts feature is hidden from the UI (no nav link). It remains reachable as
// an internal PREVIEW at the unlinked /receipts-preview path only.
import ReceiptManagement from './components/ReceiptManagement';
import AdminDashboard from './components/AdminDashboard';
import LoginPage, { ChangePasswordPage, SetPasswordPage } from './components/LoginPage';
import { apiService } from './services/api';
import { AuthContext } from './authContext';
import { appName, orgName } from './brand';

// Redirect component for backward compatibility
function PatientRedirect() {
  const { id } = useParams();
  return <Navigate to={`/individual/${id}`} replace />;
}

function PatientCarePlanRedirect() {
  const { id } = useParams();
  return <Navigate to={`/individuals/${id}/care-plan`} replace />;
}

function Homepage() {
  const navigate = useNavigate();
  const auth = useContext(AuthContext);

  return (
    <div className="App">
      {/* Header */}
      <header className="app-header">
        <div className="header-content">
          <div className="logo-section">
            <div className="logo">{appName()}</div>
          </div>
          <nav className="nav-menu">
            <button className="nav-item" onClick={() => navigate('/')}>Dashboard</button>
            <button className="nav-item" onClick={() => navigate('/individuals')}>Support Plans</button>
            <button className="nav-item" onClick={() => navigate('/import')}>Import</button>
            {auth?.is_admin && (
              <button className="nav-item" onClick={() => navigate('/admin')}>Admin</button>
            )}
            <button
              className="nav-item"
              onClick={async () => {
                try { await apiService.logout(); } catch (_) {}
                window.location.reload();
              }}
              title="Sign out"
            >
              Sign out
            </button>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="main-content">
        <div className="welcome-section">
          <div className="welcome-text">
            <h1>Welcome to the {orgName()} Support Platform</h1>
            <h2>Support Plan Organizer & Individual Success Platform</h2>
            <p className="subtitle">
              Streamlined management dashboard for {orgName()} with
              comprehensive support plans, organized profiles, and seamless
              coordination for home-visiting support people.
            </p>
          </div>

          <div className="action-cards">
            <div className="card primary-card">
              <div className="card-icon">📋</div>
              <h3>Support Plan Viewer</h3>
              <p>Access all support plan outlines, profiles, and comprehensive data in one centralized location.</p>
              <button className="card-button primary" onClick={() => navigate('/individuals')}>View All Plans</button>
            </div>

            <div className="card">
              <div className="card-icon">🤖</div>
              <h3>AI Import System</h3>
              <p>Upload existing support plan documents and let AI automatically extract and organize the information.</p>
              <button className="card-button" onClick={() => navigate('/import')}>Import Support Plan</button>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="app-footer">
        <div className="footer-content">
          <div className="footer-text">
            <span>© 2026 {orgName()} — {appName()}</span>
          </div>
          <div className="footer-links">
            <a href="mailto:thomas@strategylab.ca">Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

/**
 * AuthGate — checks /auth/check on mount, then either:
 *   - shows the login page if not authenticated
 *   - shows the change-password screen if must_change_password is set
 *   - renders children otherwise
 *
 * Also exposes the current auth state via React context so the rest of the
 * app can show username + a logout button without re-fetching.
 */
function AuthGate({ children }) {
  const [state, setState] = useState({ loading: true, auth: null });

  const refresh = useCallback(async () => {
    try {
      const auth = await apiService.authCheck();
      setState({ loading: false, auth });
    } catch (e) {
      setState({ loading: false, auth: { authenticated: false } });
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (state.loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280' }}>
        Loading…
      </div>
    );
  }

  if (!state.auth?.authenticated) {
    return <LoginPage onAuthenticated={refresh} />;
  }

  if (state.auth.must_change_password) {
    return <ChangePasswordPage onChanged={refresh} />;
  }

  return <AuthContext.Provider value={state.auth}>{children}</AuthContext.Provider>;
}


function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Homepage />} />
      <Route path="/individuals" element={<PatientDashboard />} />
      <Route path="/individual/:id" element={<PatientCarePlan />} />
      <Route path="/individuals/:id/care-plan" element={<PatientCarePlan />} />
      {/* Backward compatibility redirects */}
      <Route path="/patients" element={<Navigate to="/individuals" replace />} />
      <Route path="/patient/:id" element={<PatientRedirect />} />
      <Route path="/patients/:id/care-plan" element={<PatientCarePlanRedirect />} />
      <Route path="/import" element={<ImportCarePlan />} />
      {/* Receipts hidden from the UI — the old /receipts path redirects home,
          but the feature stays reachable as an internal PREVIEW at the
          unlinked /receipts-preview path (not shown in any navigation). */}
      <Route path="/receipts" element={<Navigate to="/" replace />} />
      <Route path="/receipts-preview" element={<ReceiptManagement preview />} />
      <Route path="/admin" element={<AdminDashboard />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        {/* Opened from an emailed link (sign-up, invitation, forgot-password),
            usually signed out — so it sits outside the auth gate. Success
            signs in and navigates to /, where the gate mounts fresh. */}
        <Route path="/set-password" element={<SetPasswordPage />} />
        <Route path="*" element={<AuthGate><AppRoutes /></AuthGate>} />
      </Routes>
    </Router>
  );
}

export default App;
